import {
  ChangeDetectorRef,
  Component,
  Injector,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { PrFormSelectListComponent } from '@app/components/pr-form/pr-form-select-list/pr-form-select-list.component';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrModalComponent } from '@app/components/pr-modal/pr-modal/pr-modal.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { API_ROUTE_SUPPORTS_SEARCH } from '@app/forms/subscription/api/api-router';
import { InfoOption } from '@app/forms/subscription/components/info/info.component';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { infoOption } from '@app/forms/subscription/services/ref';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { combineLatest, Observable, Subject, debounceTime, map, mergeMap, takeUntil, tap } from 'rxjs';
import { SupportsTable } from '../../shared/supports-table/supports-table.model';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

@UntilDestroy()
@Component({
    selector: 'reinvestment-stock-form',
    templateUrl: './reinvestment-stock-form.component.html',
    styleUrls: ['./reinvestment-stock-form.component.scss'],
    standalone: false
})
export class ReinvestmentStockFormComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  optionInfo1: InfoOption = infoOption;

  supportsTable: SupportsTable = {
    title: 'Supports de réinvestissement',
    addLabel: 'Ajouter un support',
    removable: true,
    removeComment:
      'Toute suppression est définitive, les données seront perdues.',
    totalLabel: 'Total réparti',
    targetPercent: 100,
    emptyLabel: 'Aucun support de réinvestissement sélectionné.',
    getTypeLabel: (typeId: string) => this.getSupportTypeLabel(typeId),
    columns: [
      { label: 'Support', type: 'support' },
      {
        label: 'En %',
        type: 'input',
        controlName: 'repartitionStock',
        unit: '%',
        digits: '1.0-4',
        step: '0.0001',
        min: 0,
        max: 100,
        ariaLabel: 'Réinvestissement en pourcentage',
        total: true,
      },
      {
        label: 'En €',
        type: 'input',
        controlName: 'amountRepartitionStock',
        unit: '€',
        digits: '1.2-2',
        step: '0.01',
        min: 0,
        max: () => this.amountToReinvestStock || 0,
        ariaLabel: 'Réinvestissement en euros',
        total: true,
      },
    ],
  };

  @ViewChild('formSelectList', {
    static: true,
    read: PrFormSelectListComponent,
  })
  formSelectList: PrFormSelectListComponent;

  @ViewChild('modalSupport', { static: true, read: PrModalComponent })
  modalSupport: PrModalComponent;

  criteriaSupportForm: UntypedFormGroup;
  supportTypes;
  // Libellés français des types de support (référentiel), indexés par id (ex. 'STRUCTURED_PRODUCTS')
  // — utilisé par supportsTable.getTypeLabel pour éviter d'afficher le code technique brut à côté
  // de l'isin dans la colonne "Support" (cf. supports-table.component.ts:supportDetail()).
  private supportTypeLabels: Record<string, string> = {};

  supports: any[] = [];

  currentSupportPage = 0;
  supportsPageSize = 15;
  supportsLength = 0;

  private destroy$ = new Subject<void>();

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    protected injector: Injector,
    private cdr: ChangeDetectorRef,
    private referenceDao: ReferenceDao,
    private httpApi: HttpApiService,
  ) {
    super(formConstructor);
    this.criteriaSupportForm = this.formBuilder.group({
      autocomplete: [],
      supportTypes: [],
      enableViewAll: [false],
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngOnInit() {
    this.subscriptionFacade.initForm(this);
    this.formSelectList.loading = true;
    this.supportTypes = combineLatest([
      this.referenceDao.getSupportTypes(),
      this.search(),
    ]).pipe(
      map(([supportTypes, search]) => supportTypes),

      tap(data => {}),
    );

    this.criteriaSupportForm.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => (this.formSelectList.loading = true)),
        debounceTime(500),
        mergeMap(this.search.bind(this)),
      )
      .subscribe();

    this.referenceDao
      .getSupportTypes()
      .pipe(untilDestroyed(this))
      .subscribe(types => {
        this.supportTypeLabels = Object.fromEntries(
          (types || []).map((type: any) => [type.id, type.value]),
        );
      });
  }

  getSupportTypeLabel(typeId: string): string {
    return this.supportTypeLabels[typeId] || typeId;
  }

  search(): Observable<any> {
    const searchOptions = this.buildSearchOptions();

    return this.searchSupports(searchOptions).pipe(
      takeUntil(this.destroy$),
      map(res => {
        this.formSelectList.loading = false;
        this.supportsLength = res.totalElements;
        this.supports = res.content || [];
        return res;
      }),
      tap(() => {
        this.cdr.detectChanges();
      }),
    );
  }

  changeSupportsPage(event) {
    this.currentSupportPage = event.pageIndex;
    this.formSelectList.loading = true;
    this.search().subscribe();
  }

  private searchSupports(searchOptions: any): Observable<any> {
    return this.httpApi
      .post(API_ROUTE_SUPPORTS_SEARCH, {
        ...searchOptions,
        active: true,
      })
      .pipe(takeUntil(this.destroy$));
  }

  private buildSearchOptions(): any {
    return {
      page: this.currentSupportPage + 1,
      size: this.supportsPageSize,
      autocomplete: this.criteriaSupportForm.value.autocomplete,
      supportTypes: this.criteriaSupportForm.value.supportTypes
        ? [this.criteriaSupportForm.value.supportTypes]
        : undefined,
      lifeInsuranceId: this.productId,
      noStrike: !this.criteriaSupportForm.value.enableViewAll,
      amendmentMapped: true,
      isAvailableInSimulationAndSubscription: true,
      fillFullData: true,
    };
  }

  onSubmit() {
    this.form.markAsDirty();
    // PRE-4896 : préremplit les 4 champs "*Autocomplete" de la slide "Taux par support" (arbitrage),
    // pour tous les supports sélectionnés ici, quelle que soit l'organisation.
    this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  disabledNextBtn() {
    return this.hasNotReinvestmentStockFull();
  }

  hasNotReinvestmentStockFull() {
    return !this.isFullyReinvested;
  }

  disinvestAll() {}

  totalStockValue() {
    return (
      this.products.get(`${0}`).get('parameters').get('currentAmountStock')
        .value || 0
    );
  }

  addSupport() {
    this.modalSupport.open();
  }

  validateAddSupport() {
    this.modalSupport.close();
  }

  getPerf(support: any): string {
    if (support?.details?.performances?.perfP1Y)
      return (support?.details?.performances?.perfP1Y * 100).toFixed(2) + ' %';
    else return '~';
  }
  getPerfColor(support: any): string {
    let color = 'neutral';
    if (support?.details?.performances?.perfP1Y) {
      if (support?.details?.performances?.perfP1Y > 0.05) color = 'success';
      else if (support?.details?.performances?.perfP1Y < -0.05)
        color = 'danger';
      else color = 'warning';
    }
    return color;
  }
  getRisk(support: any): string {
    if (support?.details?.profilRisk?.value)
      return support?.details?.profilRisk?.value;
    else return '~';
  }
  getRiskColor(support: any): string {
    let color = 'neutral';
    if (support?.details?.profilRisk?.value) {
      if (support?.details?.profilRisk?.value >= 5) color = 'danger';
      else if (support?.details?.profilRisk?.value >= 3) color = 'warning';
      else color = 'success';
    }
    return color;
  }

  onSupportSelect(support) {
    let newControl = this.formBuilder.group(this.createModel(support));
    let formArray = this.supportsInvestmentStock as UntypedFormArray;
    newControl.setValue({ ...newControl.value, index: formArray.value.length });
    formArray.push(newControl);
    // set parent form as dirty
    this.form.markAsDirty();
  }

  onSupportDeselect(support) {
    let formArray = this.supportsInvestmentStock as UntypedFormArray;
    let control = formArray.controls.findIndex(
      c => c.value.id == support.id || c.value.isin == support.isin,
    );
    formArray.removeAt(control);
    this.form.markAsDirty();
  }

  getSupportInvestmentStock(isin) {
    const currentSupports = this.supportsInvestmentStock.value;
    return _.map(currentSupports, 'isin').includes(isin);
  }

  onMoreInfoClick(support) {
    window.open(`#/dashboard/assets/supports/${support.id}`, '_blank');
  }
}
